import type { CommissionType } from "@/lib/commission-catalog.api";
import type { CommissionEditRequest, CommissionTransaction } from "@/lib/commission.api";
import type { User } from "@/lib/users.api";
import type { CommissionEditRequestStatusFilter } from "@/hooks/queries/useCommissionEditRequests";
import type { RequestContext } from "./commissionEditQueue.types";

export interface RequestStats {
  total: number;
  pending: number;
  approved: number;
  rejected: number;
}

export function buildTransactionMap(
  transactions: CommissionTransaction[],
): Record<number, CommissionTransaction> {
  return Object.fromEntries(transactions.map((transaction) => [transaction.id, transaction]));
}

export function buildUserMap(users: User[]): Record<number, User> {
  return Object.fromEntries(users.map((user) => [user.id, user]));
}

export function buildRequestContexts({
  requests,
  transactions,
  users,
  types,
}: {
  requests: CommissionEditRequest[];
  transactions: Record<number, CommissionTransaction>;
  users: Record<number, User>;
  types: CommissionType[];
}): RequestContext[] {
  return requests.map((request) => {
    const transaction = transactions[request.commissionTransactionId];
    const commissionType = transaction
      ? types.find((type) => type.code === transaction.type)
      : undefined;
    return {
      request,
      transaction,
      requester: users[request.requestedBy],
      reviewer: request.reviewedBy ? users[request.reviewedBy] : undefined,
      commissionType,
      typeLabel: commissionType?.label,
    };
  });
}

export function filterRequestContexts({
  contexts,
  search,
  statusFilter,
  typeFilter,
}: {
  contexts: RequestContext[];
  search: string;
  statusFilter: CommissionEditRequestStatusFilter;
  typeFilter: string;
}): RequestContext[] {
  const needle = search.trim().toLowerCase();

  return contexts.filter((context) => {
    const { request, requester, reviewer, transaction, typeLabel } = context;
    const matchesStatus = statusFilter === "all" || request.status === statusFilter;
    const matchesType =
      typeFilter === "__all__" || transaction?.type === typeFilter || typeLabel === typeFilter;
    const searchable = [
      request.id,
      request.commissionTransactionId,
      request.reason,
      request.reviewNote,
      requester?.fullName,
      reviewer?.fullName,
      typeLabel,
      transaction?.type,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

    return matchesStatus && matchesType && (!needle || searchable.includes(needle));
  });
}

export function getRequestStats(requests: CommissionEditRequest[]): RequestStats {
  return {
    total: requests.length,
    pending: requests.filter((request) => request.status === "pending").length,
    approved: requests.filter((request) => request.status === "approved").length,
    rejected: requests.filter((request) => request.status === "rejected").length,
  };
}
